import voteTypes from 'constants/voteTypes';
import voteValues from 'constants/voteValues';
import {
  getPartnerFromParticipant,
  getRepresentativeFromParticipant,
} from 'modules/boards/utils';
import { getPartnerNDPercent } from 'utils/filters';

import {
  Board,
  BoardOrder,
  BoardParticipant,
  Partner,
  PartnerRepresentant,
  Society,
} from 'types';
import { TranslateFn } from 'hooks/useTranslate';

type GetBoardVotesExportDataProps = {
  board: Board;
  actualSociety: Society;
  partners: Partner[];
  partnerRepresentants: PartnerRepresentant[];
  orderId?: string;
  t: TranslateFn;
};

// Replicates the vote badge text shown in BoardVotesTable (filterVote.ts)
const getVoteText = (
  participant: BoardParticipant,
  order: BoardOrder,
  t: TranslateFn
): string => {
  const voteData = participant?.votes?.find((v) => v?.order === order?.['_id']);

  if (!voteData) return t('-');

  if (order?.voteType === voteTypes.YES_NO_ABS.value) {
    if (voteData?.vote === voteValues.YES.value) return t('Yes');
    if (voteData?.vote === voteValues.NO.value) return t('Nope');
    return t('Abstention');
  }

  return voteData?.vote || t('-');
};

export const getBoardVotesExportData = ({
  board,
  actualSociety,
  partners,
  partnerRepresentants,
  orderId,
  t,
}: GetBoardVotesExportDataProps) => {
  const isSpecificOrder = Boolean(orderId && orderId !== 'default_option');

  const orders = isSpecificOrder
    ? (board?.orders || []).filter((o) => o?.['_id'] === orderId)
    : board?.orders || [];

  const rows: Record<string, string | number>[] = [];

  board.participants.forEach((participant: BoardParticipant) => {
    const partner = getPartnerFromParticipant(participant, partners);

    const hasShares = (partner?.sharesCount?.actual || 0) > 0;
    const isNotAbsent = !participant?.isAbsent;

    // Same filtering applied by getParticipantRows for the rendered table
    if (!hasShares || !isNotAbsent) return;

    const representative = getRepresentativeFromParticipant(
      participant,
      partners,
      partnerRepresentants
    );

    const percentage = getPartnerNDPercent(partner, actualSociety) || 0;

    const row: Record<string, string | number> = {
      [t('Partner')]: participant.name,
      [t('Shares')]: partner?.sharesCount?.actual || 0,
      'ND%': +percentage.toFixed(3),
      [t('RepresentativeBoard')]: representative?.name || '-',
    };

    orders.forEach((order, index) => {
      const columnName = isSpecificOrder
        ? t('Vote')
        : `${index + 1}. ${order.subject}`;
      row[columnName] = getVoteText(participant, order, t);
    });

    rows.push(row);
  });

  return rows;
};
